
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Unlock, Shield, ExternalLink, AlertTriangle } from 'lucide-react';
import { usePageAccess } from '@/hooks/usePageAccess';
import { useNavigate } from 'react-router-dom';

const PAGE_DISPLAY_NAMES = {
  guides: 'Tutorials & Guides',
  parts: 'Parts Database',
  tools: 'Calculator Tools',
  roadmap: 'Feature Roadmap',
  donate: 'Donate Page'
};

const PageAccessControl = () => {
  const { pageAccess, loading, updatePageAccess, lockAllPages } = usePageAccess();
  const [updatingPage, setUpdatingPage] = useState<string | null>(null);
  const [lockingAll, setLockingAll] = useState(false);
  const navigate = useNavigate();

  const handleToggle = async (pageName: string, isCurrentlyAvailable: boolean) => {
    console.log(`🎛️ Toggle clicked for ${pageName}. Currently available: ${isCurrentlyAvailable}`);
    
    setUpdatingPage(pageName);
    
    // If currently available (unlocked), we want to lock it
    // If currently locked, we want to unlock it
    const shouldLock = isCurrentlyAvailable;
    
    console.log(`📝 Setting ${pageName} to ${shouldLock ? 'LOCKED' : 'UNLOCKED'}`);
    
    const success = await updatePageAccess(pageName, shouldLock);
    
    if (!success) {
      console.error(`❌ Failed to update ${pageName}`);
    }
    
    setUpdatingPage(null);
  };

  const handleLockAll = async () => {
    console.log('🔒 Lock All button clicked');
    setLockingAll(true);
    await lockAllPages();
    setLockingAll(false);
  };

  const getAvailableCount = () => {
    // Count pages that are NOT locked (available)
    return Object.values(pageAccess).filter(isLocked => !isLocked).length;
  };

  const getTotalPages = () => {
    return Object.keys(pageAccess).length;
  };

  const getLockedCount = () => {
    return Object.values(pageAccess).filter(Boolean).length;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Page Access Control
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalPages = getTotalPages();
  const availableCount = getAvailableCount();
  const lockedCount = getLockedCount();

  console.log('📊 Page access stats:', { totalPages, availableCount, lockedCount, pageAccess });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Page Access Control
            </CardTitle>
            <CardDescription>
              Control which pages are accessible to non-admin users
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <Unlock className="h-3 w-3" />
              {availableCount}/{totalPages} Available
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Test Link and Lock All Button */}
        <div className="flex justify-between items-center">
          <Button
            onClick={() => window.open('/under-construction', '_blank')}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Test Under Construction Page
          </Button>
          
          <Button
            onClick={handleLockAll}
            disabled={lockingAll || lockedCount === totalPages}
            variant="destructive"
            size="sm"
          >
            <Lock className="h-4 w-4 mr-2" />
            {lockingAll ? 'Locking All...' : 'Lock All Sections'}
          </Button>
        </div>

        {/* Debug Information (remove in production) */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Debug Info</span>
          </div>
          <div>Current State: {JSON.stringify(pageAccess)}</div>
          <div>Available: {availableCount}, Locked: {lockedCount}, Total: {totalPages}</div>
        </div>

        {/* Page Controls */}
        <div className="space-y-4">
          {Object.entries(PAGE_DISPLAY_NAMES).map(([pageName, displayName]) => {
            const isLocked = pageAccess[pageName] || false;
            const isAvailable = !isLocked; // Invert for display
            const isUpdating = updatingPage === pageName;

            console.log(`🎯 Rendering ${pageName}: locked=${isLocked}, available=${isAvailable}, updating=${isUpdating}`);

            return (
              <div key={pageName} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  {isLocked ? (
                    <Lock className="h-5 w-5 text-red-500" />
                  ) : (
                    <Unlock className="h-5 w-5 text-green-500" />
                  )}
                  <div>
                    <h3 className="font-medium">{displayName}</h3>
                    <p className="text-sm text-muted-foreground">
                      /{pageName === 'guides' ? 'tutorials' : pageName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge 
                    variant={isAvailable ? "secondary" : "destructive"}
                    className={`min-w-[80px] justify-center ${
                      isAvailable ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''
                    }`}
                  >
                    {isAvailable ? 'Available' : 'Locked'}
                  </Badge>
                  <Switch
                    checked={isAvailable} // Toggle shows availability, not lock status
                    onCheckedChange={() => handleToggle(pageName, isAvailable)}
                    disabled={isUpdating}
                    aria-label={`Toggle access for ${displayName}`}
                  />
                  {isUpdating && (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary"></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PageAccessControl;
